import { User } from '../../../../../../models/User'
import { CRMWorkflow, CRMWorkflowTemplate, WealthboxWorkflow } from '../../../../../../models/Integration'
import React, { useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { TrackEvent, useTracking } from '../../../common/tracking'
import { useExecuteAiAgentMutation } from '../../../../../../store/ai/aiAgent'
import { SuggestedWorkflowExecutionResult } from '../../../../../../models/SuggestedWorkflow'
import { addToast } from '../../../../../../store/toast/toastSlice'
import { AIAgentType } from '../../../../../../models/AIAgent'
import { Spinner } from 'react-bootstrap'
import { Divider } from '../../../../../../components/Divider/Divider'
import classNames from 'classnames'
import { Button } from '../../../../../../components/Button/Button'

import { CRMWorkflowTemplateView } from './CRMWorkflowTemplateView'
import { CRMWorkflowView } from './CRMWorkflowView'

export interface SuggestedWorkflowsProps {
  roomId: string
  sessionId: string
  serviceId: string
  contactId: string
  contact: User
  runningWorkflows: CRMWorkflow[]
  workflowTemplates: CRMWorkflowTemplate[]
  onWorkflowUpdated: (workflow: CRMWorkflow) => void
  onWorkflowStarted: (workflow: CRMWorkflow) => void
  isSelectedExternalContact: boolean
}

export const SuggestedWorkflows: React.FC<SuggestedWorkflowsProps> = ({
  roomId,
  sessionId,
  serviceId,
  contactId,
  contact,
  runningWorkflows,
  workflowTemplates,
  onWorkflowUpdated,
  onWorkflowStarted,
  isSelectedExternalContact,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const [isLoading, setLoading] = useState<boolean>(true)
  const [suggestedRunningWorkflows, setSuggestedRunningWorkflows] = useState<CRMWorkflow[]>([])
  const [completedRunningWorkflows, setCompletedRunningWorkflows] = useState<CRMWorkflow[]>([])
  const [suggestedNewWorkflows, setSuggestedNewWorkflows] = useState<CRMWorkflowTemplate[]>([])
  const [startedSuggestedNewWorkflows, setStartedSuggestedNewWorkflows] = useState<CRMWorkflowTemplate[]>([])
  const [animateWorkflowStepBackground, setAnimateWorkflowStepBackground] = useState<number | null>(null)
  const [suggestedWorkflowExecutionResult, setSuggestedWorkflowExecutionResult] = useState<SuggestedWorkflowExecutionResult | null>(null)

  const handleWorkflowStarted = (workflow: CRMWorkflow, workflowTemplate: CRMWorkflowTemplate) => {
    setStartedSuggestedNewWorkflows([...startedSuggestedNewWorkflows, workflowTemplate])
    onWorkflowStarted(workflow)
  }

  const handleWorkflowUpdated = (workflow: CRMWorkflow) => {
    setCompletedRunningWorkflows([...completedRunningWorkflows, workflow])
    dispatch(addToast({ variant: 'success', title: 'Success', messages: ['The workflow has been updated.'] }))
    onWorkflowUpdated(workflow)
  }

  const executeSuggestedWorkflow = useCallback(async () => {
    setLoading(true)
    setStartedSuggestedNewWorkflows([])
    try {
      const integration = contact.integrations?.find((i) => i.serviceId === serviceId)
      const suggestedWorkflowExecutionResult = (await triggerExecuteAiAgent({
        accountId,
        payload: {
          type: AIAgentType.SUGGESTED_WORKFLOW,
          context: {
            ...(isSelectedExternalContact ? { serviceId: serviceId, externalId: integration?.externalId } : { contactId: contactId }),
            roomId,
            sessionId,
          },
        },
      }).unwrap()) as SuggestedWorkflowExecutionResult
      setSuggestedWorkflowExecutionResult(suggestedWorkflowExecutionResult)
    } finally {
      setLoading(false)
    }
  }, [triggerExecuteAiAgent, isSelectedExternalContact, accountId, serviceId, contactId, contact, roomId, sessionId])

  useEffect(() => {
    executeSuggestedWorkflow()
  }, [executeSuggestedWorkflow])

  useEffect(() => {
    if (suggestedWorkflowExecutionResult) {
      setSuggestedRunningWorkflows(
        runningWorkflows
          .filter((workflow) => !completedRunningWorkflows.map((completed) => completed.id).includes(workflow.id))
          .filter((workflow) => suggestedWorkflowExecutionResult.result.workflows.map((w) => `${w.id}`).includes(`${workflow.id}`)),
      )

      const suggestedTemplates = workflowTemplates
        .filter((template) => !startedSuggestedNewWorkflows.map((newWorkflow) => newWorkflow.id).includes(template.id))
        .filter((template) => {
          const id = 'redtailOneId' in template ? template.redtailOneId : template.id
          return suggestedWorkflowExecutionResult.result.workflowTemplates.map((t) => `${t.id}`).includes(`${id}`)
        })

      setSuggestedNewWorkflows(suggestedTemplates)
    }
  }, [suggestedWorkflowExecutionResult, runningWorkflows, workflowTemplates, startedSuggestedNewWorkflows, completedRunningWorkflows])

  const handleRerun = tracked(executeSuggestedWorkflow, {
    eventName: TrackEvent.SUGGESTED_WORKFLOW_RERUN,
    eventDetails: {
      roomId,
      sessionId,
      contactId,
      serviceId,
    },
  })

  if (isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <div className='d-flex flex-column gap-3'>
        {suggestedNewWorkflows.length
          ? suggestedNewWorkflows.map((workflowTemplate) => {
              return (
                <CRMWorkflowTemplateView
                  key={`suggested-workflow-template-${workflowTemplate.id}`}
                  workflowTemplate={workflowTemplate}
                  serviceId={serviceId}
                  contact={contact}
                  onWorkflowStarted={(workflow) => handleWorkflowStarted(workflow, workflowTemplate)}
                  suggestedLabel={
                    suggestedWorkflowExecutionResult?.result.workflowTemplates.find(
                      (suggestedTemplate) => `${suggestedTemplate.id}` === `${workflowTemplate.id}`,
                    )?.label ?? ''
                  }
                />
              )
            })
          : null}
      </div>
      {suggestedNewWorkflows.length && suggestedRunningWorkflows.length ? <Divider /> : null}
      {suggestedRunningWorkflows.length
        ? suggestedRunningWorkflows.map((workflow) => (
            <div
              className={classNames({ 'workflow-step-background-animation': animateWorkflowStepBackground === workflow.id })}
              onAnimationEnd={() => setAnimateWorkflowStepBackground(null)}
            >
              <CRMWorkflowView
                workflow={workflow as WealthboxWorkflow}
                serviceId={serviceId}
                contact={contact}
                onWorkflowStepCompleted={handleWorkflowUpdated}
              />
            </div>
          ))
        : null}
      {suggestedNewWorkflows.length + suggestedRunningWorkflows.length === 0 ? 'Looks like everything is up to date.' : null}
      <div className='d-flex justify-content-end'>
        <Button className='button-medium py-0' onClick={handleRerun}>
          <i className='bi bi-arrow-clockwise' />
          Rerun
        </Button>
      </div>
    </>
  )
}
